import * as React from 'react'
import { SubmissionTypes } from '@oneblink/types'
import { localisationService } from '@oneblink/apps'
import { useIsMounted, useDrafts } from '@oneblink/apps-react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import {
  ItemContainer,
  SubTitle,
  DeleteButton,
} from 'components/ListComponents'
import SwipeContainer from 'components/SwipeContainer'
import Modal from 'components/Modal'
import LoadingSpinner from 'components/LoadingSpinner'
import ErrorModal from 'components/Modals/ErrorModal'

const ButtonsContainer = styled.div`
  margin-left: auto;
  display: flex;
  align-items: flex-end;
  margin-bottom: 0.5rem;
`
ButtonsContainer.displayName = 'ButtonsContainer'

const DraftTitle = styled.span`
  font-weight: bold;
`
DraftTitle.displayName = 'DraftTitle'

export default function DraftsListItem({
  draft,
  disabled,
}: {
  draft: SubmissionTypes.FormsAppDraft
  disabled: boolean
}) {
  const isMounted = useIsMounted()

  const resumeLink = `/forms/${draft.formId}?draftId=${draft.draftId}`

  const draftDate = React.useMemo(() => {
    if (draft.createdAt) {
      const d = new Date(draft.createdAt)
      return localisationService.formatDatetime(d)
    }
  }, [draft.createdAt])

  const { deleteDraft } = useDrafts()

  const [{ isDeleting, isConfirming, error }, setState] = React.useState<{
    isDeleting: boolean
    isConfirming: boolean
    error: Error | null
  }>({
    isDeleting: false,
    isConfirming: false,
    error: null,
  })

  const clearError = React.useCallback(() => {
    setState((currentState) => ({
      ...currentState,
      error: null,
    }))
  }, [])

  const handleDeleteDraft = React.useCallback(async () => {
    if (isMounted.current) {
      setState({
        isConfirming: true,
        isDeleting: true,
        error: null,
      })
    }

    let newError = null

    try {
      await deleteDraft(draft.draftId)
    } catch (error) {
      newError = error as Error
    }

    if (isMounted.current) {
      setState({
        isConfirming: true,
        isDeleting: false,
        error: newError,
      })
    }
  }, [deleteDraft, draft.draftId, isMounted])

  const confirmDelete = React.useCallback(() => {
    setState({
      isConfirming: true,
      isDeleting: false,
      error: null,
    })
  }, [])

  return (
    <SwipeContainer onSwipeRight={confirmDelete} disabled={disabled}>
      <ItemContainer className="fa-drafts__list-item">
        <div>
          <div>
            <DraftTitle className="fa-drafts__draft-name">
              {draft.title}
            </DraftTitle>
          </div>
          <SubTitle className="fa-drafts__draft-subtitle">
            Saved: {draftDate}
          </SubTitle>
        </div>
        <ButtonsContainer>
          <DeleteButton
            onClick={confirmDelete}
            $disabled={disabled}
            className="fa-drafts__delete-button"
          />
          <Link to={resumeLink} className="fa-drafts__resume-link">
            Resume
          </Link>
        </ButtonsContainer>

        {!!error && <ErrorModal error={error} onClose={clearError} />}
        {isDeleting && (
          <Modal
            className="fa-modal__deleting-draft"
            isOpen={isDeleting}
            title="Deleting Draft"
            cardClassName="has-text-centered"
            actions={null}
          >
            <LoadingSpinner />
          </Modal>
        )}
        <Modal
          isOpen={isConfirming}
          title="Delete Draft"
          className="fa-modal__delete-draft"
          actions={
            <>
              <button
                type="button"
                className="button ob-button is-light"
                onClick={() =>
                  setState((currentState) => ({
                    ...currentState,
                    isConfirming: false,
                  }))
                }
              >
                Cancel
              </button>
              <button
                type="button"
                className="button ob-button is-primary"
                onClick={handleDeleteDraft}
                disabled={isDeleting}
              >
                Delete
              </button>
            </>
          }
        >
          <p>Are you sure you wish to delete this draft?</p>
        </Modal>
      </ItemContainer>
    </SwipeContainer>
  )
}
